import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ludolio/shared-ui';
import { getErrorMessage, labsApi } from '@/lib/api';
import { useLabs } from '@/lib/labs-context';
import { UnitSelect } from '@/components/UnitSelect';
import { initialUnitChoice, unitChoiceToId } from '@/lib/units';
import type { LabProjectDetail, LabTeamSummary } from '@/lib/types';

const NEW_TEAM = 'new';

export function CreateProjectDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (project: LabProjectDetail) => void;
}) {
  const labs = useLabs();
  const [title, setTitle] = useState('');
  const initialUnit = initialUnitChoice(labs);
  const [unitId, setUnitId] = useState(initialUnit);
  const [teams, setTeams] = useState<LabTeamSummary[]>([]);
  const [teamChoice, setTeamChoice] = useState(NEW_TEAM);
  const [teamName, setTeamName] = useState('');
  const [teamNameEdited, setTeamNameEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setUnitId(initialUnit);
    setTeams([]);
    setTeamChoice(NEW_TEAM);
    setTeamName('');
    setTeamNameEdited(false);
    setError('');
    let active = true;
    const load = async () => {
      const collected: LabTeamSummary[] = [];
      let nextPage = 1;
      let total = 0;
      do {
        const result = await labsApi.listTeams(labs.organization.slug, nextPage, 100);
        collected.push(...result.teams);
        total = result.total;
        nextPage += 1;
      } while (active && collected.length < total && nextPage <= 10);
      return collected;
    };
    load()
      .then((result) => {
        if (active) setTeams(result);
      })
      .catch((err) => {
        if (active) setError(getErrorMessage(err));
      });
    return () => {
      active = false;
    };
  }, [initialUnit, labs.organization.slug, open]);

  useEffect(() => {
    if (!teamNameEdited) setTeamName(title);
  }, [teamNameEdited, title]);

  const create = async () => {
    setError('');
    setSaving(true);
    try {
      const unit = unitChoiceToId(unitId);
      const project = teamChoice === NEW_TEAM
        ? await labsApi.createProject(labs.organization.slug, {
          title: title.trim(),
          unitId: unit,
          teamName: (teamName || title).trim(),
        })
        : await labsApi.createProject(labs.organization.slug, {
          title: title.trim(),
          unitId: unit,
          teamId: teamChoice,
        });
      onCreated(project);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            void create();
          }}
        >
          <DialogHeader>
            <DialogTitle>New project</DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="project-title">Title</Label>
            <Input id="project-title" value={title} maxLength={120} onChange={(event) => setTitle(event.target.value)} autoFocus />
          </div>
          <div className="space-y-2">
            <Label htmlFor="project-team">Team</Label>
            <Select value={teamChoice} onValueChange={setTeamChoice}>
              <SelectTrigger id="project-team">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NEW_TEAM}>New team</SelectItem>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {teamChoice === NEW_TEAM && (
            <div className="space-y-2">
              <Label htmlFor="project-team-name">Team name</Label>
              <Input
                id="project-team-name"
                value={teamName}
                maxLength={120}
                onChange={(event) => {
                  setTeamNameEdited(true);
                  setTeamName(event.target.value);
                }}
              />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="project-unit">Unit</Label>
            <UnitSelect id="project-unit" value={unitId} onChange={setUnitId} />
          </div>
          {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving || !title.trim() || (teamChoice === NEW_TEAM && !(teamName || title).trim()) || (labs.requiresLabsUnit && !unitChoiceToId(unitId))}>
              {saving ? 'Creating…' : 'Create project'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
